import { createFileRoute } from "@tanstack/react-router";
import { useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { Button } from "@/components/ui/button";
import { useTranslate } from "@/hooks/use-translate";
import {
  InputField,
  LightSelectField,
  TextAreaField,
} from "@/components/ui/form";
import { cn, formatNumber, unPick } from "@/lib/utils";
import type { StockBatch } from "@/store/StockBatch";
import { useEffect, useState } from "react";
import SelectStockBatch from "@/components/stock-batch/select-stock-batch";
import { Plus } from "lucide-react";
import {
  SupplyReturnStatusActions,
  SupplyReturnStatusColors,
  SupplyReturnStatusMap,
} from "@/components/supply-return/supply-return-meta";
import type { PaymentInfo } from "@/store/FinanceAccount";
import ProcessPayment from "@/components/ui/shared/process-payment";

export const Route = createFileRoute(
  "/(dasboard)/inventory/supply-returns/create"
)({
  component: CreateSupplyReturnPage,
});

function CreateSupplyReturnPage() {
  const store = useAppStore();
  const { t, formatDate } = useTranslate();
  const [stock_batch, setStockBatch] = useState<StockBatch>();

  const form = useSimpleForm({
    quantity: 0,
    amount: 0,
    reason: "",
    status: "pending",
    payments: [],
  });

  useEffect(() => {
    if (store.auth.session) {
      store.stockBatch.fetch({});
      store.financeAccount.fetch({});
    }
  }, [store.auth.session]);

  const getAmount = () => {
    return (stock_batch?.unit_price || 0) * form.values.quantity;
  };

  const getRemainingAmount = () => {
    return (
      parseFloat(form.values.amount) -
      form.values.payments
        .map((item: PaymentInfo) => parseInt(item.amount as any))
        .reduce((sum: number, a: number) => sum + a, 0)
    );
  };

  const addPayment = () => {
    form.setValue("payments", [
      ...form.values.payments,
      {
        finance_account_id: "",
        amount: getRemainingAmount(),
        due_date: "",
        payment_date: formatDate(new Date(), "yyyy-MM-dd"),
      },
    ]);
  };

  const submit = async () => {
    store.errors.reset();
    const data = {
      ...unPick(form.values, ["payments"]),
      stock_batch_id: stock_batch?.id,
    };

    if (form.values.payments.length) {
      data["payments"] = form.values.payments.map((item: any) => item);
    }
    store.supplyReturn
      .create(data)
      .then(() => {
        store.supplyReturn.fetch();
        window.history.back();
      })
      .catch(store.errors.catch);
  };

  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-800 mb-4">
        {t("Create New Supply Return")}
      </h2>
      <>{store.errors.render()}</>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <aside className="bg-white rounded-md border border-gray-200 p-6">
          <header className="text-lg font-semibold">Product to return </header>
          <article className="mt-2">
            <SelectStockBatch
              label="Stock item"
              stock_batch={stock_batch}
              items={store.stockBatch.items}
              onSelected={(stock_batch: StockBatch | undefined) => {
                setStockBatch(stock_batch);
                if (stock_batch)
                  form.setValue("sender_batch_id", stock_batch.id);
              }}
            />

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4  col-span-2">
                <InputField
                  label={t("Quantity")}
                  name="quantity"
                  type="number"
                  value={form.values.quantity}
                  onChange={(e: any) => {
                    const value = e.target.value;
                    store.errors.reset();
                    if (
                      parseFloat(value) >
                      (parseFloat(stock_batch?.quantity as any) || 0)
                    ) {
                      store.errors.set(
                        "quantity",
                        `The available quantity is ${stock_batch?.quantity}`
                      );
                    } else {
                      form.setValue("quantity", value);
                    }
                  }}
                  error={store.errors.values.quantity}
                />

                <div className="h-12 ">
                  <label className="mb-1.5 block text-sm font-medium text-gray-900">
                    Cost
                  </label>
                  <div className="bg-secondary-100 border-gray-200 border px-2 py-1.5 rounded-md">
                    {formatNumber(getAmount())}
                    {" " + store.auth?.session?.shop?.currency}
                  </div>
                </div>
              </div>

              <InputField
                label={t("Amount to refund")}
                name="amount"
                type="number"
                className="col-span-2"
                value={form.values.amount}
                onChange={form.handleChange}
                error={store.errors.values.amount}
              />

              <TextAreaField
                name="reason"
                className="col-span-2"
                value={form.values.reason}
                onChange={form.handleChange}
                label={"Description / Observation"}
                rows={2}
              />
            </div>
          </article>
        </aside>

        <aside className="p-6 bg-white rounded-md border border-gray-200">
          <header className="text-lg font-semibold">Processing</header>
          <article className="mt-2">
            <div className="space-x-4">
              <label className="font-medium text-sm block">Status</label>
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    "rounded-full px-8 w-[150px] py-1 text-sm",
                    SupplyReturnStatusColors[form.values.status]
                  )}
                >
                  {t(SupplyReturnStatusMap[form.values.status])}
                </div>
                <LightSelectField
                  name="status"
                  className="w-full"
                  value={form.values.status}
                  onChange={(e: any) => {
                    const value = e.target.value;
                    if (value == "approved") {
                      addPayment();
                    } else {
                      form.setValue("payments", []);
                    }
                    form.setValue("status", value);
                  }}
                  options={SupplyReturnStatusActions.filter((item) =>
                    ["pending", "approved"].includes(item.value)
                  )}
                />
              </div>
            </div>
            {form.values.status == "approved" && (
              <>
                <ProcessPayment
                  net_amount={parseFloat(form.values.amount)}
                  form={form}
                  accountClass="col-span-2"
                  allowCashDetails={false}
                />
                <Button
                  onClick={addPayment}
                  className="mt-2 border-none bg-transparent text-primary-500 font-semibold hover:underline hover:bg-transparent px-0"
                >
                  <Plus className="w-4 h-4" />
                  Add Payment
                </Button>
              </>
            )}
          </article>
        </aside>
      </div>

      {/* Submission Button */}
      <Button className="mt-4 py-2" onClick={submit}>
        {t("Submit Supply Return")}
      </Button>
    </section>
  );
}
